import React, { useState } from "react";
import {
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Modal,
  Divider,
  Table,
  TableProps,
  Space,
  message,
} from "antd";
import { Employee } from "../../core/interfaces/Employee";
import { fetchEmpDetails } from "../../core/services/employee-service";
import { SearchOutlined } from "@ant-design/icons";
import { ReportSummaryRequest } from "../../core/interfaces/reportSummaryRequest";
import { useNavigate } from "react-router-dom";
import { appInsights } from "../../core/services/insight.service";
import GlobalErrorMessage from "../global-error/GlobalErrorMessage";

const { Option } = Select;

interface ReportSpecsSummaryProps {}

const ReportSpecsSummary: React.FC<ReportSpecsSummaryProps> = () => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [username, setUserName] = useState("");
  const [modalInputValue, setModalInputValue] = useState("");
  const [empData, setEmpData] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRequesterName, setSelectedRequesterName] = useState<string>(""); // New state for the selected name
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();

  const setRequester = (text: string, name:string) => {
    setUserName(text);
    setSelectedRequesterName(name);
    setOpen(false);
    setEmpData([]);
  };

  const columns: TableProps<Employee>["columns"] = [
    {
      title: "S/N",
      dataIndex: "index",
      key: "index",
      render: (text: string, record: Employee, index: number) =>
        `${index + 1}.`,
      width: "15%",
    },
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (text) => <a>{text}</a>,
      width: "20%",
    },
    {
      title: "User ID/Email ID",
      dataIndex: "emailAddress",
      key: "emailAddress",
      align: "center",
      width: "20%",
    },
    {
      title: "PhoneExt",
      dataIndex: "phoneExt",
      key: "phoneExt",
      width: "15%",
    },

    {
      title: "Department",
      dataIndex: "deptName",
      key: "deptName",
      width: "20%",
    },
    {
      title: "Location",
      dataIndex: "locationName",
      key: "locationName",
      width: "20%",
    },
  ];

  const produceReport = async (values: ReportSummaryRequest) => {
    navigate("/report-summary", {
      state: {
        AccountNumber: values.AccountNumber ?? "",
        FromRequestNumber: values.FromRequestNumber ?? "",
        ToRequestNumber: values.ToRequestNumber ?? "",
        FromRequestDate: values.FromRequestDate?.toString() ?? "",
        ToRequestDate: values.ToRequestDate?.toString() ?? "",
        RequesterCai: username ?? null,
        RequestStatus: values.RequestStatus ?? null,
      },
    });
  };

  const SearchUser = async (text: string) => {
    setLoading(true);
    try {
      const response = await fetchEmpDetails(text); // Adjust the API endpoint as needed
      setEmpData(response);
    } catch (error) {
      setApiError(error);
      appInsights.trackEvent({ name: "FetchUsersFailed", properties: { error: error } });
    } finally {
      setLoading(false);
    }
  };

    const showModal = async (e: any) => {
      setModalInputValue(e);
      setOpen(true);
      if (e && e.trim() !== "") {
        await SearchUser(e); 
      } else {
        setEmpData([]);
      }
    };

  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleModalInputChange = (e: any) => {
    setModalInputValue(e);
  };

  const handleRetry = () => {
    setApiError(null);
  };

  return (
    <>
      <Breadcrumb
        items={[
          {
            title: "reports",
          },
          {
            title: "report-summary",
          },
        ]}
      />
      {apiError ? (
          <GlobalErrorMessage 
          error={apiError}
          visible={!!apiError}
          onClose={() => setApiError(null)}
          onRetry={handleRetry}
        />
        ) : (
      <div className="div-container">
        <Row gutter={16}>
          <Col span={12} offset={1}>
            <div>
              <p>Note: You can click on <SearchOutlined /> for search window</p>
            </div>
            <Form form={form} layout="vertical" onFinish={produceReport}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Request Number From"
                    name="FromRequestNumber"
                  >
                    <Input placeholder="From" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Request Number To" name="ToRequestNumber">
                    <Input placeholder="To" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Request Date From" name="FromRequestDate">
                    <DatePicker format="MM/DD/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Request Date To" name="ToRequestDate">
                    <DatePicker format="MM/DD/YYYY" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Account" name="AccountNumber">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    label="Requester"
                    name="RequesterCai"
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                    <Input
                      value={username}
                      placeholder="input search text"
                      onChange={(e) => setUserName(e.target.value)}
                      style={{ width: 200 }}
                    />
                    <a onClick={() => showModal(username)}>
                      <SearchOutlined />
                    </a>
                    {selectedRequesterName && ( // Display the selected name if it exists
      <span style={{ marginLeft: 10 }}>
        {selectedRequesterName}
      </span>
    )}
    </div>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="type"
                    name="RequestStatus"
                  >
                    <Select>
                      <Option value={null}> </Option>
                      <Option value="1">ACCOUNT</Option>
                      <Option value="2">BUSINESS UNIT</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Produce Report
                </Button>
                <Button
                  type="default"
                  style={{ marginLeft: "10px" }}
                  onClick={() => window.history.back()}
                >
                  Back
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>

        <Modal
          title="Search For Employee"
          open={open}
          onOk={handleOk}
          onCancel={handleCancel}
          width={1000}
        >
          <Divider></Divider>
          <Space direction="vertical">
            <Row gutter={[16, 32]}>
              <Col span={16}>
                <div>Enter Search Query</div>
              </Col>
            </Row>
            <Row gutter={[16, 32]}>
              <Col span={18}>
                <div>
                  <Input
                    value={modalInputValue}
                    onChange={(e) => handleModalInputChange(e.target.value)}
                  />
                </div>
              </Col>
              <Col span={6}>
                <div>
                  <Button
                    type="primary"
                    onClick={() => SearchUser(modalInputValue)}
                  >
                    Search
                  </Button>
                </div>
              </Col>
            </Row>
          </Space>

          <Table
            dataSource={empData}
            columns={columns}
            pagination={false}
            scroll={{ y: 150 }}
            loading={loading}
            locale={{
              emptyText:
                "No matching record found. Kindly refine your criteria!",
            }}
            onRow={(record) => ({
              onClick: () => setRequester(record.emailAddress, record.fullName),
            })}
            style={{ marginTop: "20px" }}
            size="small"
          />

          <Divider></Divider>
        </Modal>
      </div>)}
    </>
  );
};

export default ReportSpecsSummary;
